import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SectionHeading from '../components/SectionHeading';
import EntryCard from '../components/EntryCard';
import TipsterLogo from '../components/TipsterLogo';
import CtaBand from '../components/CtaBand';
import { useData } from '../context/DataContext';
import { monthKey, monthLabel, summarize } from '../lib/tips';

export default function ResultsPage() {
  const { tipsters, entriesFor, loading } = useData();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get('t') || '');
  const [month, setMonth] = useState('all');

  useEffect(() => {
    if (!active && tipsters.length) setActive(tipsters[0].id);
  }, [tipsters, active]);

  const tipster = tipsters.find(t => t.id === active);
  const list = useMemo(() => (tipster ? entriesFor(tipster.id) : []), [tipster, entriesFor]);
  const months = useMemo(() => [...new Set(list.map(e => monthKey(e.date)))], [list]);
  const visible = month === 'all' ? list : list.filter(e => monthKey(e.date) === month);
  const s = summarize(visible);

  const pick = (id) => { setActive(id); setMonth('all'); setParams({ t: id }); };

  return (
    <>
      <Header />
      <section className="bg-navy text-white py-16 md:py-24 relative overflow-hidden grain">
        <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-royal/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading dark eyebrow="Results archives" title="Every release, every day — one archive per tipster." lead="Doubles show both legs with automatically calculated combined odds. Singles show the line, the price and the result. No-pick days are recorded too." />
          <div className="mt-10 grid sm:grid-cols-3 gap-3" data-testid="results-tipster-tabs">
            {tipsters.map(t => {
              const st = summarize(entriesFor(t.id));
              const on = t.id === active;
              return (
                <button key={t.id} onClick={() => pick(t.id)} className={`text-left rounded-2xl p-4 border transition-all flex items-center gap-4 ${on ? 'bg-white text-navy border-white' : 'bg-white/[0.06] border-white/10 hover:bg-white/10'}`} data-testid={`results-tab-${t.id}`}>
                  <TipsterLogo tipster={t} className="w-11 h-11" />
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{t.name}</div>
                    <div className={`text-[11px] font-mono ${on ? 'text-slate-500' : 'text-white/60'}`}>{st.won}W – {st.lost}L · avg {st.avgOdds}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {tipster && (
        <section className="py-14 md:py-20">
          <div className="max-w-5xl mx-auto px-5 md:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-3">
                <TipsterLogo tipster={tipster} className="w-10 h-10" />
                <div>
                  <h2 className="font-display text-2xl font-extrabold text-navy">{tipster.name}</h2>
                  <Link to={`/tipsters/${tipster.id}`} className="text-xs font-semibold text-royal hover:underline" data-testid="results-profile-link">View profile & plans →</Link>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2" data-testid="results-kpis">
                {[['Released', s.released, 'released'], ['Won', s.won, 'won'], ['Lost', s.lost, 'lost'], ['Avg odds', s.avgOdds, 'avg']].map(([l, v, id]) => (
                  <div key={l} className="rounded-2xl bg-mist border border-line px-4 py-3 text-center" data-testid={`kpi-${id}`}><div className="font-mono text-xl font-bold text-navy">{v}</div><div className="text-[11px] text-slate-500">{l}</div></div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="month-filter">
              <button onClick={() => setMonth('all')} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${month === 'all' ? 'bg-navy text-white border-navy' : 'bg-white text-slate-600 border-line hover:border-royal'}`} data-testid="month-all">All</button>
              {months.map(m => <button key={m} onClick={() => setMonth(m)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${month === m ? 'bg-navy text-white border-navy' : 'bg-white text-slate-600 border-line hover:border-royal'}`} data-testid={`month-${m}`}>{monthLabel(m)}</button>)}
            </div>

            <div className="mt-6 grid gap-3" data-testid="archive-list">
              {loading && <div className="text-sm text-slate-500">Loading archive…</div>}
              {!loading && visible.length === 0 && <div className="text-sm text-slate-500">No entries recorded for this period yet.</div>}
              {visible.map(e => <EntryCard key={e.id} entry={e} />)}
            </div>
          </div>
        </section>
      )}
      <CtaBand />
      <Footer />
    </>
  );
}
